import { useBreakpointDown } from '@all-manager/design-system-v2';
import { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { useMemo, useState } from 'react';
import { ExpenseCategoryDatabase, ExpenseDatabase } from '../../../../types';
import { getSeriesColor, SLICE_BORDER_COLOR } from '../../utils/chartTheme';
import { getCategoryTotals, getExpenseYears } from '../../utils/expenseStats';
import { formatAmount, formatShare } from '../../utils/formatAmount';

type Props = {
  categories: ExpenseCategoryDatabase[];
  expenses: ExpenseDatabase[];
};

export function useYearBreakdown(props: Props) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const isCompact = useBreakpointDown('md');

  const years = useMemo(() => getExpenseYears(props.expenses, currentYear), [props.expenses, currentYear]);
  const totals = useMemo(
    () => getCategoryTotals(props.expenses, props.categories, selectedYear),
    [props.expenses, props.categories, selectedYear],
  );

  const chartData: ChartData<'pie', number[], string> = {
    labels: totals.map((item) => item.name),
    datasets: [{
      data: totals.map((item) => item.total),
      backgroundColor: totals.map((_, index) => getSeriesColor(index)),
      borderColor: SLICE_BORDER_COLOR,
      borderWidth: 2,
    }],
  };

  const chartOptions: ChartOptions<'pie'> = {
    responsive: true,
    aspectRatio: isCompact ? 1 : 1.6,
    plugins: {
      legend: {
        position: isCompact ? 'bottom' : 'right',
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'pie'>) => {
            const item = totals[context.dataIndex];

            return item ? `${formatAmount(item.total)} (${formatShare(item.share)})` : '';
          },
        },
      },
    },
  };

  function handleYearChange(year: number) {
    setSelectedYear(year);
  }

  return {
    years,
    selectedYear,
    totals,
    chartData,
    chartOptions,
    handleYearChange,
  };
}
